import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import ArrowForwardIcon from 'components/material-symbol/icons/ArrowForward'
import CheckCircleIcon from 'components/material-symbol/icons/CheckCircleOutline'
import EmailIcon from 'components/material-symbol/icons/MailOutline'
import FileUploadIcon from 'components/material-symbol/icons/FileUpload'
import RouterLinkButton from 'components/router-link-button'
import VideoFilePicker from 'components/video-file-picker'
import { useAuth } from 'lib/auth'
import { centsToDollarsFormatted } from 'lib/utils/number'
import StepsList from './steps-list'
import { useStyles } from './styles'
import {
  trackVirtualCoachingStudentEvent,
  VIRTUAL_COACHING_STUDENT_EVENTS,
} from './analytics'
import UploadForm from './upload-form'
import {
  formatTurnaroundWithin,
  getCoachFirstName,
  getRequestPath,
  getUploadPath,
  MAX_VIDEOS,
  REQUEST_STATUS,
} from './utils'

// Longest the CTA waits on the session load before falling back to the link.
export const AUTH_CHECK_TIMEOUT_MS = 5000

export const getNextSteps = request => {
  const { coach, virtualCoaching } = request.lessonType || {}
  const turnaround = formatTurnaroundWithin(virtualCoaching?.turnaroundHours)

  return [
    {
      title: 'Upload Your Videos',
      caption: `Record your swing and upload up to ${MAX_VIDEOS} videos for analysis`,
    },
    {
      title: 'Coach Reviews',
      caption: `${getCoachFirstName(coach)} will analyze your swing${
        turnaround ? ` ${turnaround}` : ''
      }`,
    },
    {
      title: 'Get Feedback',
      caption: 'Receive detailed feedback and improvement tips',
    },
  ]
}

// Shown after Stripe returns the golfer. `request` is the
// `VirtualCoachingRequest` the backend created for the payment. "Upload Your
// Video" opens the picker, and once videos are chosen the shared `UploadForm`
// takes over. This route has no auth gate and Stripe's return is a full page
// load, so the session is loaded here (silently, no forced sign-in) and the
// CTA waits for it, up to `AUTH_CHECK_TIMEOUT_MS`. A signed-out golfer (or one
// whose session load stalls) gets a link to `/upload`, which forces sign-in
// before any upload. `initialUploads` seeds the form (dev preview).
const PurchaseConfirmed = ({ request, initialUploads }) => {
  const classes = useStyles()
  const { isLoggedIn, login } = useAuth()
  const [authChecked, setAuthChecked] = useState(Boolean(isLoggedIn))
  const [selection, setSelection] = useState(null)
  const { lessonType, orderNumber } = request

  useEffect(() => {
    if (isLoggedIn || !login) {
      setAuthChecked(true)
      return undefined
    }
    let active = true
    const done = () => active && setAuthChecked(true)
    const timer = setTimeout(done, AUTH_CHECK_TIMEOUT_MS)
    Promise.resolve(login(done)).catch(done)
    return () => {
      active = false
      clearTimeout(timer)
    }
    // Load the session once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Revisiting the page after uploading: point at the request instead.
  const canUpload =
    !request.status || request.status === REQUEST_STATUS.PURCHASED
  const canPickHere = canUpload && Boolean(isLoggedIn)
  const showForm =
    canPickHere && (selection?.files.length > 0 || initialUploads?.length > 0)
  const pickErrors = selection?.errors || []

  useEffect(() => {
    if (!canPickHere) return
    trackVirtualCoachingStudentEvent(
      VIRTUAL_COACHING_STUDENT_EVENTS.UPLOAD_VIEWED,
      { requestId: request.id },
    )
    // One view per request, as on the upload page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPickHere, request.id])

  return (
    <Box
      className={clsx(classes.narrowPage, classes.confirmPage)}
      data-testid="virtual-coaching-confirmed"
    >
      <Box className={classes.confirmHero}>
        <Box
          className={clsx(classes.confirmIcon, classes.confirmIconSuccess)}
          aria-hidden="true"
        >
          <CheckCircleIcon />
        </Box>
        <Typography variant="h1" className={classes.confirmTitle}>
          Purchase Confirmed!
        </Typography>
        <Typography className={clsx(classes.muted, classes.confirmSubtitle)}>
          Thank you for your purchase. You&apos;re one step closer to improving
          your game.
        </Typography>
      </Box>

      <section className={classes.card} aria-labelledby="order-details-title">
        <Box className={classes.cardHeader}>
          <Box className={classes.cardTitleBlock}>
            <Typography
              id="order-details-title"
              variant="h2"
              className={classes.cardTitleText}
            >
              Order Details
            </Typography>
            {orderNumber ? (
              <Typography className={classes.cardSubtitle}>
                Order #{orderNumber}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <Box className={classes.cardBody}>
          <Box className={classes.detailRow}>
            <Typography className={classes.orderItemName}>
              {lessonType?.title}
            </Typography>
            <Typography className={classes.orderItemPrice}>
              {centsToDollarsFormatted(lessonType?.priceInCents)}
            </Typography>
          </Box>
          {lessonType?.coach?.name ? (
            <Box className={classes.detailRow}>
              <Typography className={classes.muted}>Coach</Typography>
              <Typography>{lessonType.coach.name}</Typography>
            </Box>
          ) : null}
        </Box>
      </section>

      <Box className={classes.noticeCard}>
        <Box className={classes.noticeIcon} aria-hidden="true">
          <EmailIcon />
        </Box>
        <Box>
          <Typography className={classes.stepTitle}>Email Sent</Typography>
          <Typography className={classes.stepCaption}>
            We&apos;ve sent a confirmation email to your inbox with all the
            details and instructions for uploading your video.
          </Typography>
        </Box>
      </Box>

      <section className={classes.card} aria-labelledby="next-steps-title">
        <Box className={classes.cardHeader}>
          <Box className={classes.cardTitleBlock}>
            <Typography
              id="next-steps-title"
              variant="h2"
              className={classes.cardTitleText}
            >
              Next Steps
            </Typography>
            <Typography className={classes.cardSubtitle}>
              Here&apos;s what happens next
            </Typography>
          </Box>
        </Box>
        <Box className={classes.cardBody}>
          <StepsList steps={getNextSteps(request)} currentStep={1} />
        </Box>
      </section>

      {showForm ? (
        <UploadForm
          branded
          request={request}
          initialSelection={selection}
          initialUploads={initialUploads}
        />
      ) : (
        <Box className={classes.centerAction}>
          {canUpload && !authChecked ? (
            <VideoFilePicker
              disabled
              onSelect={setSelection}
              label="Upload Your Videos"
              startIcon={<FileUploadIcon />}
              endIcon={<ArrowForwardIcon />}
              variant="contained"
              size="large"
            />
          ) : canPickHere ? (
            <VideoFilePicker
              multiple
              onSelect={setSelection}
              label="Upload Your Videos"
              startIcon={<FileUploadIcon />}
              endIcon={<ArrowForwardIcon />}
              variant="contained"
              size="large"
              inputTestId="virtual-coaching-confirmed-input"
            />
          ) : (
            <RouterLinkButton
              variant="contained"
              color="primary"
              size="large"
              to={
                canUpload
                  ? getUploadPath(request.id)
                  : getRequestPath(request.id)
              }
              startIcon={canUpload ? <FileUploadIcon /> : undefined}
              endIcon={<ArrowForwardIcon />}
            >
              {canUpload ? 'Upload Your Videos' : 'View Your Virtual Coaching'}
            </RouterLinkButton>
          )}
          {pickErrors.length > 0 ? (
            <Typography color="error" variant="body2" role="alert">
              {pickErrors
                .map(({ file, message }) => `${file.name}: ${message}`)
                .join(' ')}
            </Typography>
          ) : null}
          {canPickHere ? (
            <Typography variant="body2" className={classes.muted}>
              Or use the link in your confirmation email
            </Typography>
          ) : null}
        </Box>
      )}
    </Box>
  )
}

PurchaseConfirmed.propTypes = {
  initialUploads: PropTypes.array,
  request: PropTypes.shape({
    id: PropTypes.string.isRequired,
    orderNumber: PropTypes.string,
    status: PropTypes.string,
    lessonType: PropTypes.shape({
      title: PropTypes.string,
      priceInCents: PropTypes.number,
      virtualCoaching: PropTypes.object,
      coach: PropTypes.object,
    }),
  }).isRequired,
}

export default PurchaseConfirmed
